"use client";

import * as React from "react";
import { cn } from "@/utils";

interface PageHeroProps {
  title: string;
  subtitle?: string;
  badge?: string;
  className?: string;
  children?: React.ReactNode;
}

export function PageHero({
  title,
  subtitle,
  badge,
  className,
  children,
}: PageHeroProps) {
  return (
    <section
      className={cn(
        "relative mb-10 animate-fade-up overflow-hidden rounded-2xl border border-white/[0.07] bg-surface-200/60 p-8 shadow-card backdrop-blur-md md:p-10 lg:p-12",
        className,
      )}
    >
      <div
        className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-hex-400/10 blur-3xl"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-hex-400/[0.04] via-transparent to-hex-500/[0.03]"
        aria-hidden
      />

      <div className="relative z-10 max-w-4xl">
        {badge && (
          <div className="mb-4 flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-hex-400 opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-hex-400" />
            </span>
            <span className="label-caps">{badge}</span>
          </div>
        )}
        <h1 className="text-4xl font-bold tracking-tightest text-white md:text-5xl lg:text-6xl">
          {title.split(" ").length > 1 ? (
            <>
              {title.split(" ")[0]}{" "}
              <span className="hex-gradient-text">
                {title.split(" ").slice(1).join(" ")}
              </span>
            </>
          ) : (
            <span className="hex-gradient-text">{title}</span>
          )}
        </h1>
        {subtitle && (
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-foreground md:text-lg">
            {subtitle}
          </p>
        )}
        {children}
      </div>

      <div className="hex-bar absolute inset-x-0 bottom-0 opacity-60" />
    </section>
  );
}

export default PageHero;
